import { Globe, Phone } from "lucide-react";
import { formatCurrency } from "@wtc/ui";
import type { Companies } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderFact, HeaderMetric, HeaderSubtitleSegment } from "../model";

/** Guarded against GetCompany by detail-header-model-coverage.test.ts. */
export const COMPANY_HEADER_FIELDS = [
  "industry",
  "city",
  "state",
  "website",
  "organization_phone",
  "organization_revenue",
  "total_deal_value",
  "monthly_rate",
  "hourly_rate",
  "active_deal_count",
] as const satisfies readonly (keyof Companies)[];

export type CompanyHeaderData = Pick<Companies, (typeof COMPANY_HEADER_FIELDS)[number]>;

/**
 * 1:1 scalar columns edited in the header (dropped from the inline panel).
 * `organization_phone` is intentionally NOT here — its config marks it
 * read-only, so it stays a read-only header fact.
 */
export const COMPANY_HEADER_EDITABLE_FIELDS = [
  "website",
  "organization_revenue",
  "monthly_rate",
  "hourly_rate",
] as const satisfies readonly (keyof Companies)[];
type CompanyHeaderEditableField = (typeof COMPANY_HEADER_EDITABLE_FIELDS)[number];

function normalizeWebsiteHref(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}

export function buildCompanyHeaderModel(company: CompanyHeaderData): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  if (company.industry) subtitleSegments.push({ text: company.industry });
  const location = [company.city, company.state].filter(Boolean).join(", ");
  if (location) subtitleSegments.push({ text: location });

  const facts: HeaderFact[] = [];
  if (company.website) {
    facts.push({
      id: "website",
      icon: Globe,
      value: company.website,
      kind: "external",
      href: normalizeWebsiteHref(company.website),
      fieldId: "website" satisfies CompanyHeaderEditableField,
    });
  }
  if (company.organization_phone) {
    facts.push({ id: "phone", icon: Phone, value: company.organization_phone, kind: "phone" });
  }

  const metrics: HeaderMetric[] = [];
  const orgRevenue = formatCurrency(company.organization_revenue);
  if (orgRevenue) {
    metrics.push({
      id: "org-revenue",
      label: "Org revenue",
      value: orgRevenue,
      fieldId: "organization_revenue" satisfies CompanyHeaderEditableField,
    });
  }

  const dealValue = formatCurrency(company.total_deal_value);
  if (dealValue) metrics.push({ id: "deal-value", label: "Deal value", value: dealValue });

  const monthlyRate = formatCurrency(company.monthly_rate);
  if (monthlyRate) {
    metrics.push({
      id: "monthly-rate",
      label: "Monthly rate",
      value: monthlyRate,
      hint: "/mo",
      fieldId: "monthly_rate" satisfies CompanyHeaderEditableField,
    });
  }

  const hourlyRate = formatCurrency(company.hourly_rate);
  if (hourlyRate) {
    metrics.push({
      id: "hourly-rate",
      label: "Hourly rate",
      value: hourlyRate,
      hint: "/hr",
      fieldId: "hourly_rate" satisfies CompanyHeaderEditableField,
    });
  }

  if (company.active_deal_count != null) {
    metrics.push({
      id: "active-deals",
      label: "Active deals",
      value: String(company.active_deal_count),
    });
  }

  return { subtitleSegments, facts, metrics };
}
